// ingest — the source-ingest flow's pure vocabulary and folds.
//
// The host runs the impure gestures (the hidden file picker, the window
// drag-drop listeners, the FileReader, the `ports.addSource` calls); this
// module owns the shapes the batch's progress is folded through, so the flow
// state is a plain value the host renders and the folds are testable without
// a DOM. One entry per queued file, each in exactly one of three states — a
// named discriminated union, so "pending" / "done" / "failed" can never be
// conflated and every render site folds over the discriminant.
//
// Entries are keyed by `IngestId`, a per-session ordinal the host mints at
// queue time (its impure edge, like `nextEntryId`). The filename is NOT the
// key — the author may drop two files with the same name in one batch.

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type { ExternalError } from "./externalError.js";
import type { AddedSource } from "./ports.js";

// The batch-local key for one queued file (host-minted ordinal).
export type IngestId = number;

// Queued, waiting for (or in) its upload+extraction round-trip.
export type IngestPending = {
    readonly type: "pending";
    readonly id: IngestId;
    readonly filename: string;
};
// The server landed the file as a Source resource.
export type IngestDone = {
    readonly type: "done";
    readonly id: IngestId;
    readonly filename: string;
    readonly source: AddedSource;
};
// The round-trip failed — the typed boundary error, never a raw string.
export type IngestFailed = {
    readonly type: "failed";
    readonly id: IngestId;
    readonly filename: string;
    readonly error: ExternalError;
    // The raw upload handle, RETAINED so `retry_file` can re-run `addSource`
    // WITHOUT re-picking (owner decision, determinism M9 / ADR-I5). This is the
    // ONE place folded ingest state holds bytes — an accepted memory cost. The
    // happy-path entries (pending/done) stay byte-free; the File is held only
    // on a failure and released when the item is cleared — retry folds it back
    // to `pending` (which drops it), and a done-clear never carries it. Never
    // serialized: `IngestFile` is UI state, never an event payload nor a
    // recorded intent essence (a recorded ingest command keeps only {id,
    // filename}).
    readonly file: File;
};
// Refused BEFORE any upload (Win 2/3): the drop path validated the file locally
// and never sent it. Byte-free (no `File` retained — a rejection is never
// retried; the author re-adds a corrected file) and Retry-free at the view.
// Distinct from `failed`: no boundary was ever crossed, so there is no
// `ExternalError`, only the pure client verdict.
export type IngestRejected = {
    readonly type: "rejected";
    readonly id: IngestId;
    readonly filename: string;
    readonly rejection: IngestRejection;
};
export type IngestFile =
    | IngestPending
    | IngestDone
    | IngestFailed
    | IngestRejected;

export const mkIngestPending = (
    id: IngestId,
    filename: string,
): IngestPending => ({ type: "pending", id, filename });

const mkIngestRejected = (
    id: IngestId,
    filename: string,
    rejection: IngestRejection,
): IngestRejected => ({ type: "rejected", id, filename, rejection });

// Settle one pending entry. Two distinct intents (never an outcome flag):
// `settleDone` when the server landed the source, `settleFailed` on refusal.
// Settling a non-pending (or unknown) id is a no-op — outcomes are final.
export const settleDone = (
    files: readonly IngestFile[],
    id: IngestId,
    source: AddedSource,
): readonly IngestFile[] =>
    files.map((f) =>
        f.type === "pending" && f.id === id
            ? { type: "done", id: f.id, filename: f.filename, source }
            : f,
    );

export const settleFailed = (
    files: readonly IngestFile[],
    id: IngestId,
    error: ExternalError,
    file: File,
): readonly IngestFile[] =>
    files.map((f) =>
        f.type === "pending" && f.id === id
            ? { type: "failed", id: f.id, filename: f.filename, error, file }
            : f,
    );

// Re-arm one FAILED entry for another attempt (determinism M9 / ADR-I5): back
// to `pending`, dropping the retained `File` (the run effect carries it now, so
// the failed slot no longer needs to hold the bytes). An unknown/non-failed id
// is a no-op — the decider refuses that case, and this fold stays total.
export const retryPending = (
    files: readonly IngestFile[],
    id: IngestId,
): readonly IngestFile[] =>
    files.map((f) =>
        f.type === "failed" && f.id === id
            ? { type: "pending", id: f.id, filename: f.filename }
            : f,
    );

// The files still uploading, by name (the Library's "Uploading …" lines).
export const uploadingFilenames = (
    files: readonly IngestFile[],
): readonly string[] =>
    files.flatMap((f) => (f.type === "pending" ? [f.filename] : []));

// Drop DONE entries (they showed up in the library) at a batch's end. Keeps
// `pending` (a batch started while another still runs) AND, since ADR-I5,
// `failed` (retained WITH its File so `retry_file` stays available after the
// batch settles — released only when the entry is retried or explicitly
// cleared). Pre-ADR-I5 this also dropped failed; retry made failures persist.
export const clearSettled = (
    files: readonly IngestFile[],
): readonly IngestFile[] => files.filter((f) => f.type !== "done");

// Append pre-upload rejections to the batch (Win 2/3). Like `failed`, terminal
// and kept until the batch is cleared — but byte-free and Retry-free.
export const appendRejected = (
    files: readonly IngestFile[],
    entries: readonly {
        readonly id: IngestId;
        readonly filename: string;
        readonly rejection: IngestRejection;
    }[],
): readonly IngestFile[] => [
    ...files,
    ...entries.map((e) => mkIngestRejected(e.id, e.filename, e.rejection)),
];

// The largest RAW file the server will accept. Files now POST as the raw
// request body to `/v1/uploads/*` (no base64, no JSON envelope), so this is a
// straight RAW-byte limit — the ~33% base64 inflation that used to cap the
// effective file size at 24 MB is gone. 32,000,000 bytes sits comfortably under
// the server's 32 MiB (33,554,432-byte) `/v1/uploads/*` `bodyLimit`
// (MAX_UPLOAD_BYTES). Keep in step with that ceiling. Rejecting up front (Win 3)
// turns a generic server 413 into a specific, before-upload "too large" verdict.
export const MAX_SOURCE_BYTES = 32 * 1_000_000;

// The pre-upload refusal (Win 3). The file's KIND is not checked here: the
// picker and drop accept any file, and the server decides by its bytes
// (FR-3.1, `sniffSource`), refusing with `add_source_kind_not_accepted`.
export type FileTooLarge = { readonly type: "file_too_large" };
export type IngestRejection = FileTooLarge;

// The reason a queued file can't be ingested, or null when acceptable.
// Pure: reads only the byte size the shell minted, never the bytes.
export const classifyQueuedFile = (size: number): IngestRejection | null =>
    size > MAX_SOURCE_BYTES ? { type: "file_too_large" } : null;

// FR-3.2: the server's refusal (`add_source_kind_not_accepted`) names the
// supported types (`sniffSource`).
export const UNSUPPORTED_FILE_TYPE_MESSAGE: MessageDescriptor = msg`This file type isn't supported. Use a text file, Word (DOCX), PowerPoint (PPTX) or PDF.`;

// The localized one-line reason a rejection reads as (Win 1 renders it on the
// failed row). Pure — returns a Lingui MessageDescriptor, localized by the
// shell at render (mirrors externalError.ts's `toDisplay`).
export const rejectionDisplay = (r: IngestRejection): MessageDescriptor => {
    switch (r.type) {
        case "file_too_large":
            return msg`File too large — max 32 MB`;
    }
};

// True when a DataTransfer's type list says the drag carries OS files —
// the guard that keeps in-page drags (text selections, future card drags)
// from arming the drop overlay.
export const isFileDrag = (types: readonly string[]): boolean =>
    types.includes("Files");
